import { Injectable, inject } from '@angular/core';
import { LoggerService } from '../logger/logger.service';
import { OidcService } from './oidc.service';
import { toError } from '../utils/to-error';

const TOKEN_EXCHANGE_GRANT =
    'urn:ietf:params:oauth:grant-type:token-exchange';
const ACCESS_TOKEN_TYPE = 'urn:ietf:params:oauth:token-type:access_token';

/** Re-mint this many ms before the cached token actually expires. */
const EXPIRY_SKEW_MS = 30_000;

interface CachedToken {
    token: string;
    expiresAt: number;
    inflight?: Promise<string>;
}

/**
 * Mints resource-scoped access tokens on demand via RFC 8693 Token Exchange,
 * using the current session's primary (`api`) access token as the subject
 * token. Results are cached per `resource + scope` key until shortly before
 * expiry, and re-minted transparently.
 *
 * This lets the SPA talk to additional audiences (e.g. `risrai`) without a
 * second `UserManager`, refresh token, or silent-renew iframe: the exchange is
 * a single back-channel POST to the token endpoint.
 */
@Injectable({ providedIn: 'root' })
export class TokenBrokerService {
    private readonly oidc = inject(OidcService);
    private readonly logger = inject(LoggerService).scope('TokenBroker');

    private readonly cache = new Map<string, CachedToken>();

    /**
     * Returns an access token for `resource` carrying (at most) `scope`,
     * exchanging the current `api` token if no fresh cached token exists.
     *
     * @param resource Resource indicator (RFC 8707), e.g.
     *   `https://risr.global/resources/risrai`.
     * @param scope Space-delimited requested scope. The IdP may downscope this
     *   to the caller's entitlements.
     */
    async getToken(resource: string, scope: string): Promise<string> {
        const key = `${resource}\n${scope}`;
        const now = Date.now();
        const cached = this.cache.get(key);

        if (cached?.token && cached.expiresAt - EXPIRY_SKEW_MS > now) {
            return cached.token;
        }
        if (cached?.inflight) {
            return cached.inflight;
        }

        const inflight = this.exchange(resource, scope, key);
        this.cache.set(key, {
            token: cached?.token ?? '',
            expiresAt: cached?.expiresAt ?? 0,
            inflight,
        });
        return inflight;
    }

    /** Drops cached tokens (all, or only those for `resource`). */
    invalidate(resource?: string): void {
        if (!resource) {
            this.cache.clear();
            return;
        }
        for (const key of [...this.cache.keys()]) {
            if (key.startsWith(`${resource}\n`)) {
                this.cache.delete(key);
            }
        }
    }

    private async exchange(
        resource: string,
        scope: string,
        key: string,
    ): Promise<string> {
        try {
            const manager = this.oidc.userManager;
            if (!manager) {
                throw new Error('OIDC user manager not initialised');
            }
            const user = await this.oidc.user();
            const subjectToken = user?.access_token;
            if (!subjectToken) {
                throw new Error('No subject access token available');
            }

            const tokenEndpoint =
                await manager.metadataService.getTokenEndpoint(false);
            const clientId = manager.settings.client_id;

            const body = new URLSearchParams();
            body.set('grant_type', TOKEN_EXCHANGE_GRANT);
            body.set('subject_token', subjectToken);
            body.set('subject_token_type', ACCESS_TOKEN_TYPE);
            body.set('resource', resource);
            if (scope) {
                body.set('scope', scope);
            }
            body.set('client_id', clientId);

            const response = await fetch(tokenEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    Accept: 'application/json',
                },
                body: body.toString(),
            });

            if (!response.ok) {
                const detail = await response.text().catch(() => '');
                throw new Error(
                    `Token exchange failed (${response.status}): ${detail}`,
                );
            }

            const data = (await response.json()) as {
                access_token?: string;
                expires_in?: number;
            };
            const token = data.access_token;
            if (!token) {
                throw new Error('Token exchange response missing access_token');
            }
            const expiresIn =
                typeof data.expires_in === 'number' ? data.expires_in : 300;

            this.cache.set(key, {
                token,
                expiresAt: Date.now() + expiresIn * 1000,
            });
            this.logger.debug('Exchanged token', { resource, expiresIn });
            return token;
        } catch (err) {
            this.cache.delete(key);
            this.logger.error('Token exchange failed', toError(err));
            throw err;
        }
    }
}
